import type { ProductType, Prompt } from "@1js/pl-card-components";
import { isExtensionPrompt } from "@1js/pl-types";
import { useMsal } from "@azure/msal-react";
import * as React from "react";
import UAParser from "ua-parser-js";
import { MetaOSAppContext } from "../pages/metaos/MetaOSAppContext";
import { PropertyContext } from "../pages/PropertyContext.web";
import { handoffToAugloop } from "../services/handoffAugloop";
import { handoffToM365Chat } from "../services/handoffToM365Chat";
import type { HandoffFile } from "../services/workingsetfiles-client.web";
import { type SiteSetting } from "../types";
import useTelemetry from "../utils/Telemetry/useTelemetry";
import {
  type HandoffCallback,
  type HandoffCallbackResult,
  type HandoffResult,
} from "./handoff.types";
import { useFeatureEnablement } from "./useFeatureEnablement";
import { useFlightContext } from "./useFlightContext";

type ProductMappingType = Map<
  ProductType | string,
  {
    requirements: (settings: SiteSetting, prompt?: Prompt) => boolean;
    method: (
      prompt: Prompt,
      product: ProductType | string,
      host: string,
      locale: string,
      file: HandoffFile | undefined,
      abortController: AbortController
    ) => Promise<HandoffCallbackResult>;
  }
>;

/**
 * Orchestrates handoff of prompts to different products and services,
 * like M365 Chat and Office AI (Word, Excel, PowerPoint, OneNote, Outlook),
 *  to enable customers to try prompts in those products and services.
 * @returns methods to handoff prompts to different products and services
 */
export const useHandoffService = (): HandoffResult => {
  const [siteSetting] = React.useContext(PropertyContext);
  const { isFlightActive } = useFlightContext();
  const { device } = new UAParser().getResult();
  const { includesAny } = useFeatureEnablement();
  const telemetry = useTelemetry();
  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const isBusinessChatLicenseActive = includesAny(siteSetting.licenses, [
    "CopilotBusinessChat",
    "Copilot",
  ]);

  const isMobile = React.useCallback(() => {
    return (
      device.type === "mobile" ||
      device.type === "tablet" ||
      device.type === "wearable"
    );
  }, [device.type]);

  const productsInFlight = React.useMemo(() => {
    const products = [];
    if (!isMobile()) {
      if (isFlightActive("isTryInWordEnabled")) {
        products.push("Word");
      }

      if (isFlightActive("isTryInExcelEnabled")) {
        products.push("Excel");
      }

      if (isFlightActive("isTryInPowerPointEnabled")) {
        products.push("PowerPoint");
      }
    }

    return products;
  }, [isFlightActive]);

  const productMapping: ProductMappingType = React.useMemo(() => {
    const innerMap: ProductMappingType = new Map();
    innerMap.set("BizChat", {
      requirements: (settings: SiteSetting, prompt?: Prompt) =>
        (isFlightActive("isTryInBizChatEnabled") &&
          ((isFlightActive("isTryInDisableCosmosDBEnabled") &&
            prompt?.Repository !== "CosmosDB") ||
            !isFlightActive("isTryInDisableCosmosDBEnabled")) &&
          !isMobile() &&
          includesAny(settings.licenses, ["CopilotBusinessChat", "Copilot"]) &&
          (!isFlightActive("isTryIn1_1FormatEnabled") ||
            (isFlightActive("isTryIn1_1FormatEnabled") &&
              prompt?.Origin !== "User") ||
            isFlightActive("isTryIn3SPromptLinkEnabled"))) ||
        false,
      method: async (prompt: Prompt) =>
        await handoffToM365Chat(
          prompt,
          "M365Chat",
          isFlightActive("isTryInAppendM365ChatFeatures"),
          isFlightActive("isTryIn1_1FormatEnabled"),
          isFlightActive("isTryIn3SPromptLinkEnabled"),
          telemetry.sendApiActionEvent,
          instance,
          "work"
        ),
    });
    innerMap.set("CWC", {
      requirements: (settings: SiteSetting, prompt?: Prompt) =>
        (isFlightActive("isTryInCWCForBusinessChatUsersEnabled") &&
          ((isFlightActive("isTryInDisableCosmosDBEnabled") &&
            prompt?.Repository !== "CosmosDB") ||
            !isFlightActive("isTryInDisableCosmosDBEnabled")) &&
          !isMobile() &&
          //In this stage, the rollout is only to premium users until we are
          //onboarded to Eligibility API
          includesAny(settings.licenses, ["CopilotBusinessChat", "Copilot"])) ||
        false,
      method: async (prompt: Prompt) =>
        //Always route to web tab for CWC
        await handoffToM365Chat(
          prompt,
          "M365Chat",
          isFlightActive("isTryInAppendM365ChatFeatures"),
          isFlightActive("isTryIn1_1FormatEnabled"),
          isFlightActive("isTryIn3SPromptLinkEnabled"),
          telemetry.sendApiActionEvent,
          instance,
          "web"
        ),
    });

    productsInFlight.forEach((product) => {
      innerMap.set(product, {
        requirements: (settings: SiteSetting) =>
          (includesAny(settings.licenses, ["OfficeProPlus"]) &&
            isFlightActive("isTryInOfficeEnabled")) ||
          false,
        method: async (
          prompt: Prompt,
          product: string,
          host: string,
          locale: string,
          file: HandoffFile | undefined,
          abortController: AbortController
        ) =>
          await handoffToAugloop(
            host,
            locale,
            prompt,
            product,
            file,
            abortController,
            instance,
            siteSetting.user?.email
          ),
      });
    });
    return innerMap;
  }, [includesAny, isFlightActive, isMobile, productsInFlight]);

  /**
   * Method to handoff the prompt to the appropriate product or service
   * @param props.prompt - The prompt to handoff
   * @param props.destination - The destination product or service to handoff the prompt to
   * @param props.host - The host of SupportService
   * @param props.locale - The locale of the user
   * @returns handoffUrl to direct customer to in browser, or error if handoff failed
   */
  const onHandoff = async (
    props: HandoffCallback
  ): Promise<HandoffCallbackResult> => {
    const { prompt, destination, host, locale, file, abortController } = props;
    if (!productMapping.has(destination)) {
      throw new Error("UnknownDestination");
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const handoffMethod = productMapping.get(destination)!;

    return await handoffMethod.method(
      prompt,
      destination,
      host,
      locale,
      file,
      abortController
    );
  };

  const isHandoffSupported = React.useCallback(
    (product: ProductType | string, prompt?: Prompt): boolean => {
      return (
        (!isExtensionPrompt(prompt as Prompt) &&
          productMapping.has(product) &&
          productMapping.get(product)?.requirements(siteSetting, prompt)) ||
        false
      );
    },
    [productMapping, siteSetting]
  );

  const isProductNeedsFile = (product: ProductType | string) => {
    if (!productMapping.has(product)) {
      return false;
    }

    return !["BizChat", "Teams", "Topics", "CWC"].includes(product);
  };

  const chooseBestProductForHandoff = (
    prompt: Prompt
  ): ProductType | string | undefined => {
    const product = prompt.Products.find((product) =>
      isHandoffSupported(product, prompt)
    );
    // For a BusinessChatLicense user, handoff looks like this for prompt tagged with
    // BizChat -> BizChat(Work),
    // CWC & BizChat -> BizChat(work),
    // CWC -> BizChat(web)
    // product contains the first handoff supported product, choose BizChat when both CWC and BizChat are supported.
    if (product === "CWC" && isBusinessChatLicenseActive) {
      return prompt.Products.includes("BizChat")
        ? "BizChat"
        : "CWCWithBizChatLabel";
    }
    return product;
  };

  return {
    handoff: onHandoff,
    isHandoffSupported,
    isProductNeedsFile,
    chooseBestProductForHandoff,
  };
};
